import '@angular/compiler';
import { enableProdMode, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { environment } from '@rtls-environment/environment';
import { provideAppConfig, AppConfig } from '@everysens/frontend-shared-v2';
import { LocaleService } from '@rtls/core/locale/locale.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthHttpInterceptor, provideAuth0 } from '@auth0/auth0-angular';
import { TenantService } from '@rtls/core/tenant/tenant.service';
import { NavigationHistoryService } from '@rtls/shared/services/navigation-history.service';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import { AppRoutingModule } from '@rtls/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { CoreModule } from '@rtls/core/core.module';
import { SharedModule } from '@rtls/shared/shared.module';
import { buildAuth0Config } from '@rtls/config';
import { DataModule } from 'data';
import { dataServiceConfig } from '@rtls/config/data-service-config';
import { HeapAnalyticsModule } from '@everysens/heap-analytics';
import { StyleClassModule } from 'primeng/styleclass';
import { HeaderNotificationModule } from 'ui';
import { LegoAttachDocumentsModule } from '@lego/attach-documents';
import { AppComponent } from './app/app.component';
import { provideNgProgressOptions } from 'ngx-progressbar';
import Material from '@primeuix/themes/material';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { CustomTranslateLoaderService } from '@rtls/custom-translate-loader.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { angularPlugin, provideDatadogErrorHandler, provideDatadogRouter } from '@datadog/browser-rum-angular';
import * as pkg from '../package.json';

const primeBlue = definePreset(Material, {
	semantic: {
		primary: {
			50: '{blue.50}',
			100: '{blue.100}',
			200: '{blue.200}',
			300: '{blue.300}',
			400: '{blue.400}',
			500: '{blue.500}',
			600: '{blue.600}',
			700: '{blue.700}',
			800: '{blue.800}',
			900: '{blue.900}',
			950: '{blue.950}',
		},
	},
});

if (environment.production) {
	enableProdMode();
}

if (environment.name === 'production') {
	datadogRum.init({
		applicationId: environment.datadog.applicationId,
		clientToken: environment.datadog.clientToken,
		site: 'datadoghq.eu',
		service: 'generic-front-end',
		env: environment.name,
		version: pkg.version,
		profilingSampleRate: 100,
		sessionSampleRate: 100,
		sessionReplaySampleRate: 100,
		trackResources: true,
		trackUserInteractions: true,
		trackLongTasks: true,
		allowedTracingUrls: [environment.baseURL, environment.baseApiURL],
		defaultPrivacyLevel: 'allow',
		plugins: [angularPlugin({ router: true })],
	});

	datadogLogs.init({
		clientToken: environment.datadog.clientToken,
		site: 'datadoghq.eu',
		forwardErrorsToLogs: true,
		sessionSampleRate: 100,
	});
}

export function initLocale(locale: LocaleService) {
	return locale.getLanguageCode();
}

export function initDateLocale(localeId: LocaleService) {
	return localeId.getLanguageCode();
}

bootstrapApplication(AppComponent, {
	providers: [
		importProvidersFrom(
			AppRoutingModule,
			BrowserModule,
			CoreModule,
			SharedModule,
			DataModule.forRoot(dataServiceConfig),
			HeapAnalyticsModule.forRoot({ appId: environment.heapAppId }),
			StyleClassModule,
			HeaderNotificationModule,
			LegoAttachDocumentsModule.forRoot({ attachDocumentApiUrl: environment.attachDocumentApiUrl }),
		),
		{ provide: LOCALE_ID, useFactory: initLocale, deps: [LocaleService] },
		{ provide: MAT_DATE_LOCALE, useFactory: initDateLocale, deps: [LocaleService] },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthHttpInterceptor,
			multi: true,
		},
		TenantService,
		NavigationHistoryService,
		...provideAppConfig({ baseURL: environment.baseURL as AppConfig['baseURL'], app: 'app' }),
		provideNgProgressOptions({
			trickleSpeed: 200,
			min: 20,
		}),
		provideHttpClient(withInterceptorsFromDi()),
		providePrimeNG({ theme: { preset: primeBlue, options: { darkModeSelector: false } } }),
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useClass: CustomTranslateLoaderService,
				deps: [HttpBackend],
			},
			fallbackLang: 'en-GB',
		}),
		provideAnimations(),
		provideAuth0(buildAuth0Config()),
		provideDatadogRouter(),
		provideDatadogErrorHandler(),
		{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
	],
}).catch((err) => console.error(err));
